import MultipartUploadWorker from "./multipart-upload-worker.js";
import StreamPartitioner from "./stream-partitioner.js";

/**
 * @typedef {{parts: any[], count: number, success?: boolean}} MultipartWorkerResult
 */

const MIN_PART_SIZE = 5 * 2 ** 20;
const WORKER_COUNT = 5;
const MAX_PARTS_PER_WORKER = 2000;

/**
 * @typedef {{
 *   file: File,
 *   source: ReadableStream<Uint8Array>,
 *   size: number,
 *   partSize: number,
 *   retries: number,
 *   getPartSizeMultiple: () => number,
 *   getMinPartSizeMultiple: () => number,
 *   updatePartSize: (success: boolean, multiple: number) => void,
 *   onPartStart: (file: File, index: number) => Promise<{url: string, headers: {[key: string]: string}, method: "POST"|"PUT"}|void>,
 *   onPartComplete: (file: File, xhr: XMLHttpRequest) => Promise<false|any>,
 *   onPartRetry: (file: File, xhr: XMLHttpRequest, loaded: number, total: number, retry: number) => void,
 *   onProgress: (file: File, xhr: XMLHttpRequest|null, loaded: number, total: number, retry: number) => void,
 *   onComplete: (totalParts: number, parts: any[], success: boolean) => Promise<void>,
 * }} ConcurrentMultipartUploadOptions
 */

/**
 * Coordinates concurrent multipart workers for one file.
 */
export default class ConcurrentMultipartUpload {
  /**
   * @param {ConcurrentMultipartUploadOptions} options
   */
  constructor(options) {
    this.file = options.file;
    this.source = options.source;
    this.size = options.size;
    this.partSize = options.partSize;
    this.retries = options.retries;
    this.getPartSizeMultiple = options.getPartSizeMultiple;
    this.getMinPartSizeMultiple = options.getMinPartSizeMultiple;
    this.updatePartSize = options.updatePartSize;
    this.onPartStart = options.onPartStart;
    this.onPartComplete = options.onPartComplete;
    this.onPartRetry = options.onPartRetry;
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.workers = [];
    this.progress = new Map();
    this.parts = [];
    this.totalParts = 0;
    this.cancelled = false;
    this.settled = false;
  }

  /** @type {MultipartUploadWorker[]} */
  workers;
  /** @type {Map<MultipartUploadWorker, {loaded: number, retry: number}>} */
  progress;
  /** @type {any[]} */
  parts;
  /** @type {number} */
  totalParts;
  /** @type {boolean} */
  cancelled;
  /** @type {boolean} */
  settled;

  /**
   * @returns {Promise<void>}
   */
  async start() {
    const workerCount = Math.min(
      WORKER_COUNT,
      Math.max(1, Math.floor(this.size / MIN_PART_SIZE)),
    );
    const ranges = new StreamPartitioner(
      this.source,
      this.size,
      workerCount,
      MIN_PART_SIZE,
    ).partition();
    for (const [workerIndex, range] of ranges.entries()) {
      const worker = new MultipartUploadWorker({
        file: this.file,
        source: range.stream,
        offset: range.offset,
        size: range.size,
        partNumberStart: workerIndex * MAX_PARTS_PER_WORKER + 1,
        maxPartCount: MAX_PARTS_PER_WORKER,
        partSize: this.partSize,
        retries: this.retries,
        getPartSizeMultiple: this.getPartSizeMultiple,
        getMinPartSizeMultiple: this.getMinPartSizeMultiple,
        updatePartSize: this.updatePartSize,
        onPartStart: this.onPartStart,
        onPartComplete: this.onPartComplete,
        onPartRetry: this.onPartRetry,
        onProgress: (
          /** @type {MultipartUploadWorker} */ child,
          /** @type {number} */ loaded,
          /** @type {number} */ retry,
        ) => this.#onProgress(child, loaded, retry),
        onPart: () => {},
      });
      this.workers.push(worker);
      this.progress.set(worker, { loaded: 0, retry: 0 });
    }

    const results = await Promise.all(
      this.workers.map((worker) => worker.start()),
    );
    if (this.settled) {
      return;
    }
    this.settled = true;
    this.totalParts = results.reduce(
      (total, result) => total + result.count,
      0,
    );
    this.parts = results
      .flatMap((result) => result.parts)
      .sort((left, right) => left.partNumber - right.partNumber);
    const success =
      !this.cancelled && results.every((result) => result.success !== false);
    await this.onComplete(this.totalParts, this.parts, success);
  }

  abort() {
    if (this.settled) {
      return;
    }
    this.cancelled = true;
    for (const worker of this.workers) {
      worker.abort();
    }
  }

  #onProgress(
    /** @type {MultipartUploadWorker} */ worker,
    /** @type {number} */ loaded,
    /** @type {number} */ retry,
  ) {
    this.progress.set(worker, { loaded, retry });
    let totalLoaded = 0;
    let isRetry = false;
    for (const state of this.progress.values()) {
      totalLoaded += state.loaded;
      isRetry ||= state.retry !== 0;
    }
    this.onProgress(this.file, null, totalLoaded, this.size, isRetry ? 1 : 0);
  }

  /** @type {File} */
  file;
  /** @type {ReadableStream<Uint8Array>} */
  source;
  /** @type {number} */
  size;
  /** @type {number} */
  partSize;
  /** @type {number} */
  retries;
  /** @type {() => number} */
  getPartSizeMultiple;
  /** @type {() => number} */
  getMinPartSizeMultiple;
  /** @type {(success: boolean, multiple: number) => void} */
  updatePartSize;
  /** @type {(file: File, index: number) => Promise<{url: string, headers: {[key: string]: string}, method: "POST"|"PUT"}|void>} */
  onPartStart;
  /** @type {(file: File, xhr: XMLHttpRequest) => Promise<false|any>} */
  onPartComplete;
  /** @type {(file: File, xhr: XMLHttpRequest, loaded: number, total: number, retry: number) => void} */
  onPartRetry;
  /** @type {Function} */
  onProgress;
  /** @type {(totalParts: number, parts: any[], success: boolean) => Promise<void>} */
  onComplete;
}
